import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import { imageService } from '../services/ImageService';

const ImagePreviewModal = ({
  images, initialIndex, uid, onClose,
  source = null,
  galleryItems = null,
  onOpenNote = null
}) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  const isGallery = source === 'gallery';

  // Derive linked note for current image (gallery mode only)
  const currentLinkedNote = useMemo(() => {
    if (!isGallery || !galleryItems) return null;
    return galleryItems[currentIndex]?.note || null;
  }, [isGallery, galleryItems, currentIndex]);

  // Load current image
  useEffect(() => {
    const loadImage = async () => {
      setLoading(true);
      try {
        const url = await imageService.getImageURL(uid, images[currentIndex], false); // false for full image
        setImageUrl(url);
      } catch (error) {
        console.error("Failed to load image", error);
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [uid, images, currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrev = useCallback(() => {
    if (images.length <= 1) return;
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const handleNext = useCallback(() => {
    if (images.length <= 1) return;
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (!imageUrl) return;

    try {
      // Try to fetch the image as a blob to force download with correct name
      const response = await fetch(imageUrl, {
         mode: 'cors',
         cache: 'no-cache'
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;

      const ext = blob.type === 'image/jpeg' ? 'jpg' : 'webp';
      link.download = `notemail-image-${currentIndex + 1}.${ext}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

    } catch (error) {
      console.error("Download failed (likely CORS), falling back to new tab:", error);
      // Fallback: Open in new tab so the user can at least see/save it
      window.open(imageUrl, '_blank');
    }
  };

  if (!images || images.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Action Buttons (Top Right) */}
      <div className="absolute top-4 right-4 flex items-center gap-2 z-50">
        {/* Download Button */}
        {imageUrl && !loading && (
          <button
            onClick={handleDownload}
            className="p-2 text-white/70 hover:text-white bg-black/20 hover:bg-black/40 rounded-full transition-colors"
            title={t('common.download')}
          >
            <ArrowDownTrayIcon className="w-8 h-8" />
          </button>
        )}

        {/* Close Button */}
        <button
          onClick={onClose}
          className="p-2 text-white/70 hover:text-white bg-black/20 hover:bg-black/40 rounded-full transition-colors"
          title={t('common.close')}
        >
          <XMarkIcon className="w-8 h-8" />
        </button>
      </div>

      {/* Navigation - Left */}
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); handlePrev(); }}
          className="absolute left-4 p-3 text-white/70 hover:text-white bg-black/20 hover:bg-black/40 rounded-full transition-colors z-40"
        >
          <ChevronLeftIcon className="w-8 h-8" />
        </button>
      )}

      {/* Image Container */}
      <div
        className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={`Preview ${currentIndex + 1}`}
            className="max-w-full max-h-[90vh] object-contain select-none"
          />
        ) : (
            <div className="text-white">Failed to load image</div>
        )}
      </div>

      {/* Navigation - Right */}
      {images.length > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); handleNext(); }}
          className="absolute right-4 p-3 text-white/70 hover:text-white bg-black/20 hover:bg-black/40 rounded-full transition-colors z-40"
        >
          <ChevronRightIcon className="w-8 h-8" />
        </button>
      )}

      {/* Linked Note Pill (Gallery mode only) */}
      {isGallery && currentLinkedNote && (
        <div
          className={`absolute ${images.length > 1 ? 'bottom-16' : 'bottom-6'} left-1/2 transform -translate-x-1/2 z-50`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => onOpenNote?.(currentLinkedNote)}
            className="flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2.5 rounded-full shadow-lg hover:bg-white transition-colors max-w-[280px]"
          >
            <span className="text-lg flex-shrink-0">{currentLinkedNote.emoji || '📝'}</span>
            <span className="text-sm font-medium text-gray-800 truncate">
              {currentLinkedNote.title || 'Untitled'}
            </span>
          </button>
        </div>
      )}

      {/* Counter */}
      {images.length > 1 && (
        <div className={`absolute ${isGallery && currentLinkedNote ? 'bottom-28' : 'bottom-6'} left-1/2 transform -translate-x-1/2 bg-black/50 px-3 py-1 rounded-full text-white text-sm font-medium`}>
          {currentIndex + 1} / {images.length}
        </div>
      )}
    </div>
  );
};

ImagePreviewModal.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialIndex: PropTypes.number.isRequired,
  uid: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  source: PropTypes.string,
  galleryItems: PropTypes.arrayOf(PropTypes.shape({
    imageUID: PropTypes.string.isRequired,
    note: PropTypes.object.isRequired,
  })),
  onOpenNote: PropTypes.func,
};

export default ImagePreviewModal;
